/**
 * Playout instances as they cross the wire (PLAN §5.2): the part and piece
 * instances a `PlayoutStateWire` names. Wire copies of `@acp/domain/instances.js`
 * (D37: a contract package never reuses a domain schema);
 * `INSTANCE_SHAPE_PROOFS` fails to compile the day the two drift apart.
 */
import type { PartInstance, PieceInstance } from '@acp/domain/instances.js';
import { epochMsSchema, ulidSchema } from '@acp/domain/primitives.js';
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { partFieldsWireSchema, pieceFieldsWireSchema } from './rowFields.js';

/**
 * Why an instance no longer matches a definition row (D19): `deleted` — the
 * definition was deleted while on air or next; `adlib-part` — created by a
 * queued adlib, with no definition at all.
 */
// Open (D37 §4.3): the playout state is written by the production process, whose release may be newer than its reader's.
const instanceOrphanedWireSchema = openEnum(['deleted', 'adlib-part']).nullable();

/** Playout timings of a part instance, all epoch ms, null until they happen. */
const partInstanceTimingsWireSchema = z
  .object({
    take: epochMsSchema.nullable(),
    plannedStartedPlayback: epochMsSchema.nullable(),
    reportedStartedPlayback: epochMsSchema.nullable(),
    plannedStoppedPlayback: epochMsSchema.nullable(),
    reportedStoppedPlayback: epochMsSchema.nullable(),
    playOffset: z.number().int().nonnegative().nullable(),
  })
  .strict();

export const partInstanceWireSchema = z
  .object({
    instanceId: ulidSchema,
    partId: ulidSchema,
    segmentId: ulidSchema,
    rundownId: ulidSchema,
    activationId: ulidSchema,
    takeCount: z.number().int().nonnegative().nullable(),
    rehearsal: z.boolean(),
    part: partFieldsWireSchema,
    orphaned: instanceOrphanedWireSchema,
    reset: z.boolean(),
    timings: partInstanceTimingsWireSchema,
  })
  .strict();
export type PartInstanceWire = z.infer<typeof partInstanceWireSchema>;

/** Infinite-piece bookkeeping (Sofie's `PieceInstanceInfinite`). */
const pieceInstanceInfiniteWireSchema = z
  .object({
    infiniteInstanceId: ulidSchema,
    infiniteInstanceIndex: z.number().int().nonnegative(),
    infinitePieceId: ulidSchema,
    fromPreviousPart: z.boolean(),
    fromPreviousPlayhead: z.boolean(),
  })
  .strict();

export const pieceInstanceWireSchema = z
  .object({
    instanceId: ulidSchema,
    partInstanceId: ulidSchema,
    pieceId: ulidSchema,
    rundownId: ulidSchema,
    piece: pieceFieldsWireSchema,
    dynamicallyInserted: z.object({ time: epochMsSchema, adlibId: ulidSchema }).strict().nullable(),
    infinite: pieceInstanceInfiniteWireSchema.nullable(),
    plannedStartedPlayback: epochMsSchema.nullable(),
    reportedStartedPlayback: epochMsSchema.nullable(),
    plannedStoppedPlayback: epochMsSchema.nullable(),
    reportedStoppedPlayback: epochMsSchema.nullable(),
    userStopped: epochMsSchema.nullable(),
    disabled: z.boolean(),
    orphaned: instanceOrphanedWireSchema,
    reset: z.boolean(),
  })
  .strict();
export type PieceInstanceWire = z.infer<typeof pieceInstanceWireSchema>;

// One way only (D37 §4.3): every domain instance is a wire instance; a wire instance's `orphaned` is open (a newer
// engine's reason reads as `unknown`), so it is not a domain one — and nothing maps wire → domain.
const partSame: [PartInstance] extends [PartInstanceWire] ? true : false = true;
const pieceSame: [PieceInstance] extends [PieceInstanceWire] ? true : false = true;
/** Compile-time proofs that each domain type fits its wire copy above (`shapeProofs.ts`). */
export const INSTANCE_SHAPE_PROOFS = [partSame, pieceSame] as const;
